import { boot } from 'quasar/wrappers'
import { doc, onSnapshot } from 'firebase/firestore'
import { getFirebaseApp, getFirestoreDb } from 'src/services/app/firebase.app'
import { FIRESTORE_DATABASE_GLOBALS } from 'src/services/consts/firebase.const'
import { localStorageService } from 'src/services/app/storage.service'
import { log } from 'src/services/app/log.service'
import { STORAGE_KEYS, ERROR_TYPES } from 'src/services/consts/api.const'
import { useConfirm } from 'src/composables/app/system/useConfirm'
import { usePostHog } from 'src/composables/app/ui/usePostHog'
import { isLocalHost } from 'src/config'

const CHUNK_ERROR_PATTERNS = [
  'Failed to fetch dynamically imported module',
  'ChunkLoadError',
  'Loading chunk',
  'Loading CSS chunk',
  'Unable to preload CSS for',
]

let isVersionModalOpen = false

// No-op probe so a new deploy SHA can exercise version-control signaling.

export default boot(() => {
  const { captureException } = usePostHog()
  if (isLocalHost) return log.info('[Version] Version control disabled in local host')

  // Register handlers first – always active regardless of Firebase (detect 404s, chunk/asset load failures)
  if (!window._versionControlInitialized) {
    window._versionControlInitialized = true
    setupFetchInterceptor()
    setupChunkErrorHandler()
  }

  let db
  try {
    const firebaseApp = getFirebaseApp()
    db = getFirestoreDb(firebaseApp, FIRESTORE_DATABASE_GLOBALS)
    log.success('[Version] Firebase/Firestore initialized')
  } catch (err) {
    log.error('[Version] Firebase/Firestore initialization failed:', err)
    captureException(err, {
      error_type: ERROR_TYPES.FIREBASE,
      error_name: `[${ERROR_TYPES.FIREBASE}] ${err.message}`,
      original_error_message: err.message,
      error_context: 'version-control: init',
    })
    return
  }

  const versionDocRef = doc(db, 'frontend_vars', 'version_control')
  let unsubscribe = null
  let isFirstSnapshot = true
  let pendingLiveVersion = null

  function applyVersion(liveStr) {
    const storedVersion = getStoredVersion()

    if (isFirstSnapshot) {
      log.success('[Version] Initial snapshot received:', {
        liveVersion: liveStr,
        storedVersion,
      })
      if (storedVersion !== liveStr) {
        setStoredVersion(liveStr)
      }
      isFirstSnapshot = false
      return
    }

    log.info('[Version] Snapshot update received:', { liveVersion: liveStr, storedVersion })
    if (storedVersion !== liveStr) {
      handlePotentialVersionMismatch()
    }
  }

  function startListener() {
    if (unsubscribe) return

    unsubscribe = onSnapshot(
      versionDocRef,
      (snapshot) => {
        if (!snapshot.exists()) return
        const liveVersion = snapshot.data()?.['version_control_id']
        const liveStr = liveVersion ? String(liveVersion).trim() : ''
        if (!liveStr) return

        if (document.hidden && !isFirstSnapshot) {
          pendingLiveVersion = liveStr
          return
        }
        applyVersion(liveStr)
      },
      (err) => {
        log.error('[Version] Firestore listener error:', err)
        captureException(err, {
          error_type: ERROR_TYPES.FIREBASE,
          error_name: `[${ERROR_TYPES.FIREBASE}] Firestore listener failed - ${err.message}`,
          original_error_message: err.message,
          error_context: 'version-control: onSnapshot',
        })
        stopListener()
      }
    )
  }

  function stopListener() {
    if (unsubscribe) {
      unsubscribe()
      unsubscribe = null
    }
  }

  startListener()

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden && pendingLiveVersion) {
      applyVersion(pendingLiveVersion)
      pendingLiveVersion = null
    }
  })
})

function getStoredVersion() {
  const value = localStorageService.get(STORAGE_KEYS.VERSION_CONTROL_ID)
  return value != null && typeof value === 'string' ? value : null
}

function setStoredVersion(version) {
  if (version != null && typeof version === 'string') {
    localStorageService.set(STORAGE_KEYS.VERSION_CONTROL_ID, version)
  }
}

function isChunkOrAssetLoadError(error) {
  if (error == null) return false
  const strings = [
    String(error),
    error?.message,
    error?.cause != null ? String(error.cause) : '',
  ].filter(Boolean)
  return strings.some((str) => CHUNK_ERROR_PATTERNS.some((pattern) => str.includes(pattern)))
}

/**
 * Call from router.onError so every failed navigation (including retries) is handled.
 * Must be registered where the router instance is guaranteed (e.g. router/index.js).
 */
export function handleNavigationError(error) {
  if (isChunkOrAssetLoadError(error)) {
    log.info('[Version] Detected chunk loading error (router):', String(error))
    handlePotentialVersionMismatch()
  }
}

/**
 * Catch script/link load failures (e.g. chunk 404 on initial load). Route chunk failures are handled by router.onError.
 */
function setupChunkErrorHandler() {
  window.addEventListener(
    'error',
    (event) => {
      if (
        event.target &&
        (event.target.tagName === 'SCRIPT' || event.target.tagName === 'LINK') &&
        (event.target.src?.includes('/assets/') || event.target.href?.includes('/assets/'))
      ) {
        log.info('[Version] Detected asset loading error:', event.target.src || event.target.href)
        handlePotentialVersionMismatch()
        event.preventDefault()
      }
    },
    true
  )
}

/**
 * Setup fetch interceptor to detect API failures
 */
function setupFetchInterceptor() {
  const originalFetch = window.fetch.bind(window)

  window.fetch = async (...args) => {
    try {
      const response = await originalFetch(...args)
      const url = args[0]?.url || args[0]
      if (typeof url === 'string' && url.includes('/assets/')) {
        if (response.status === 404) {
          handlePotentialVersionMismatch()
        }
      }
      return response
    } catch (error) {
      const url = args[0]?.url || args[0]
      if (typeof url === 'string' && url.includes('/assets/')) {
        handlePotentialVersionMismatch()
        // Offline asset failures are a connectivity issue, not a new deploy - don't mislabel.
        error.name = !navigator.onLine ? ERROR_TYPES.OFFLINE : ERROR_TYPES.VERSION_MISMATCH
      }
      throw error
    }
  }
}

/**
 * Handle a potential version mismatch: show modal with "Refresh now" or "Not now". Page reloads only when the user clicks "Refresh now".
 */
function handlePotentialVersionMismatch() {
  if (!navigator.onLine) {
    log.info('[Version] Skipping version modal - offline (connectivity issue, not new version)')
    return
  }
  if (isVersionModalOpen) {
    log.info('[Version] Version modal already open, skipping duplicate')
    return
  }
  isVersionModalOpen = true
  log.info('[Version] Handling version mismatch')
  const { showConfirm } = useConfirm()
  showConfirm({
    title: 'Update available',
    message: 'To keep everything running smoothly, refresh the page to load the newest version.',
    icon: 'refresh-cw',
    confirmText: 'Refresh now',
    cancelText: 'Later',
    onConfirm: () => {
      window.location.reload()
    },
    onCancel: () => {
      isVersionModalOpen = false
    },
  })
}
